import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../hooks/useAuth';
import loginSessionService from '../../../utils/loginSessionService';
import { sendSecurityVerificationEmail, verifyEmailCode, clearVerificationCode } from '../../../utils/emailVerificationService';
import profileDeleteService from '../../../utils/profileDeleteService';

const SecurityTab = ({ userProfile, updateProfile }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [loginHistory, setLoginHistory] = useState([]);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteStep, setDeleteStep] = useState(1); // 1: confirmation, 2: type confirmation text

  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    two_factor_auth: false,
    show_login_history: false,
    password_expiry_days: 90,
    allowed_ip_addresses: [],
    require_team_2fa: false,
    security_questions_enabled: false
  });

  // Security questions state
  const [securityQuestions, setSecurityQuestions] = useState(
    userProfile?.security_questions?.length > 0 
      ? userProfile.security_questions 
      : [
          { id: 1, question: '', answer: '' },
          { id: 2, question: '', answer: '' },
          { id: 3, question: '', answer: '' }
        ]
  );
  
  const [editingSecurityQuestions, setEditingSecurityQuestions] = useState(false);
  const [securityAnswerVerified, setSecurityAnswerVerified] = useState(false);

  // Email verification state for security questions editing
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [enteredVerificationCode, setEnteredVerificationCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);

  // Predefined security questions
  const PREDEFINED_QUESTIONS = [
    'What is your mother\'s maiden name?',
    'What was the name of your first pet?',
    'What city were you born in?',
    'What is your favorite book?',
    'What was the make of your first car?',
    'What is the name of the street you grew up on?',
    'What is your favorite movie?',
    'What was your first job?',
    'What is your favorite color?',
    'What is the name of your best friend from childhood?'
  ];
  
  // Load security settings from user profile
  useEffect(() => {
    if (userProfile && userProfile.security_settings) {
      setSecuritySettings(userProfile.security_settings);
    }
  }, [userProfile]);

  // Sync security questions from userProfile when it changes
  useEffect(() => {
    if (userProfile?.security_questions && userProfile.security_questions.length > 0) {
      setSecurityQuestions(userProfile.security_questions);
    }
  }, [userProfile?.security_questions]);
  
  // Function to handle security settings update
  const handleSecurityUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const result = await updateProfile({
        security_settings: securitySettings
      });

      if (result.success) {
        setSuccessMessage('Security settings updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Failed to update security settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password change
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
    security_answer: ''
  });
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    setSuccessMessage('');
    
    // Check if security questions are set and verify answer
    const hasSecurityQuestions = securityQuestions.some(q => q.question && q.answer);
    if (hasSecurityQuestions && !securityAnswerVerified) {
      setErrors({ security_answer: 'Please verify your security question answer first' });
      return;
    }
    
    // Validate password
    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setErrors({ new_password: 'Password must be at least 8 characters long' });
      return;
    }

    // Check password strength
    const hasUpperCase = /[A-Z]/.test(passwordData.new_password);
    const hasLowerCase = /[a-z]/.test(passwordData.new_password);
    const hasNumbers = /\d/.test(passwordData.new_password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.new_password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setErrors({ 
        new_password: 'Password must contain uppercase, lowercase, numbers, and special characters' 
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Create a timeout promise that rejects after 30 seconds
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Password update request timed out. Please try again.')), 30000)
      );

      // Get the update promise
      const updatePromise = supabase.auth.updateUser({
        password: passwordData.new_password
      });

      // Race between the update and timeout
      const result = await Promise.race([updatePromise, timeoutPromise]);
      
      // Check for error in the result
      if (result?.error) {
        throw new Error(result.error.message || 'Failed to update password');
      }
      
      setSuccessMessage('Password updated successfully!');
      setPasswordData({
        new_password: '',
        confirm_password: '',
        security_answer: ''
      });
      setSecurityAnswerVerified(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[Password Update Error]:', error);
      setErrors({ 
        general: error?.message || 'Failed to update password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle security question answer verification
  const handleVerifySecurityAnswer = async () => {
    if (!passwordData.security_answer.trim()) {
      setErrors({ security_answer: 'Please enter your security answer' });
      return;
    }

    // Find the first set security question and verify answer (case-insensitive)
    const setQuestion = securityQuestions.find(q => q.question && q.answer);
    if (!setQuestion) {
      setErrors({ security_answer: 'No security questions configured' });
      return;
    }

    const isCorrect = passwordData.security_answer.toLowerCase().trim() === setQuestion.answer.toLowerCase().trim();
    
    if (isCorrect) {
      setSecurityAnswerVerified(true);
      setErrors({});
    } else {
      setErrors({ security_answer: 'Incorrect security answer' });
      setSecurityAnswerVerified(false);
    }
  };

  // Handle security question update
  const handleSaveSecurityQuestions = async () => {
    // Validate that at least one question is filled (first one is required)
    if (!securityQuestions[0]?.question || !securityQuestions[0]?.answer) {
      setErrors({ security_questions: 'Please fill in at least the first security question and answer' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Get the current user ID from the user profile
      if (!userProfile?.id) {
        throw new Error('User profile not found');
      }

      // Directly update the database with the security questions
      const { error } = await supabase
        .from('user_profiles')
        .update({ security_questions: securityQuestions })
        .eq('id', userProfile.id);

      if (error) {
        throw new Error(error.message || 'Failed to save security questions');
      }

      // Update parent through updateProfile callback
      if (updateProfile) {
        await updateProfile({ security_questions: securityQuestions });
      }
      
      // Show success message
      setSuccessMessage('Security questions saved successfully!');
      
      // Close editing UI after a brief delay to let parent state update
      setTimeout(() => {
        setEditingSecurityQuestions(false);
      }, 100);
      
      // Clear the success message after 4 seconds
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (error) {
      console.error('[Security Questions Update Error]:', error);
      setErrors({ security_questions: error.message || 'Failed to update security questions' });
    } finally {
      setLoading(false);
    }
  };

  // Handle security question field change
  const handleSecurityQuestionChange = (index, field, value) => {
    const updatedQuestions = [...securityQuestions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setSecurityQuestions(updatedQuestions);
  };

  // Handle sending verification email for security questions editing
  const handleSendVerificationEmail = async () => {
    try {
      setLoading(true);
      setErrors({});

      const userEmail = userProfile?.email;
      if (!userEmail) {
        throw new Error('Email not found in profile');
      }

      const result = await sendSecurityVerificationEmail(
        userEmail,
        userProfile?.company_name || userProfile?.full_name,
        'employer'
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      setVerificationCodeSent(true);
      setSuccessMessage('Verification code sent to your email');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error sending verification email:', error);
      setErrors({ email_verification: error.message || 'Failed to send verification email' });
    } finally {
      setLoading(false);
    }
  };

  // Handle email verification code verification
  const handleVerifyEmail = () => {
    try {
      setErrors({});

      if (!enteredVerificationCode || enteredVerificationCode.length !== 6) {
        setErrors({ email_verification: 'Please enter a valid 6-digit code' });
        return;
      }

      const result = verifyEmailCode(userProfile?.email, enteredVerificationCode);

      if (!result.success) {
        setErrors({ email_verification: result.error });
        return;
      }

      setEmailVerified(true);
      setSuccessMessage('Email verified! You can now edit your security questions.');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Proceed to edit mode after brief delay
      setTimeout(() => {
        setEditingSecurityQuestions(true);
        setShowEmailVerification(false);
        setEnteredVerificationCode('');
        setVerificationCodeSent(false);
      }, 500);
    } catch (error) {
      console.error('Error verifying email:', error);
      setErrors({ email_verification: 'An error occurred while verifying the code' });
    }
  };

  // Handle canceling email verification
  const handleCancelEmailVerification = () => {
    setShowEmailVerification(false);
    setEnteredVerificationCode('');
    setVerificationCodeSent(false);
    setEmailVerified(false);
    clearVerificationCode(userProfile?.email);
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }

    // Verify confirmation text
    if (deleteConfirmationText !== 'DELETE MY ACCOUNT') {
      setErrors({ delete_account: 'You must type DELETE MY ACCOUNT exactly to confirm' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const result = await profileDeleteService.deleteUserAccount(user.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete account');
      }

      // Show success message briefly
      setSuccessMessage('Your account is being deleted. Redirecting to homepage...');
      setShowDeleteConfirmation(false);

      // Redirect after brief delay to allow user to see success message
      setTimeout(() => {
        window.location.href = '/?account_deleted=true';
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      setErrors({ delete_account: error.message || 'Failed to delete account. Please try again or contact support.' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch actual login history from database
  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      setShowLoginHistory(true);
      
      const { data, error } = await supabase
        .from('login_history')
        .select('id, user_id, device_name, browser, os, location, ip_address, created_at, last_activity, is_current')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) {
        console.error('Error fetching login history:', error);
        setErrors({ general: 'Failed to load login history. Please try again.' });
        return;
      }
      
      // Transform data for display
      const formattedHistory = data.map((entry) => ({
        id: entry.id,
        device: entry.device_name || 'Unknown Device',
        browser: entry.browser || 'Unknown Browser',
        os: entry.os || 'Unknown OS',
        location: entry.location || 'Unknown Location',
        ip_address: entry.ip_address || 'N/A',
        timestamp: new Date(entry.last_activity || entry.created_at),
        status: 'success',
        is_current: entry.is_current || false
      }));
      
      setLoginHistory(formattedHistory);
    } catch (error) {
      console.error('[SecurityTab] Error fetching login history:', error);
      setErrors({ general: 'An unexpected error occurred while fetching login history.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOutAllDevices = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      
      // Log this sign out event
      await supabase
        .from('login_history')
        .update({ is_current: false })
        .eq('user_id', user?.id)
        .eq('is_current', true);
      
      // User will be redirected to login
      setSuccessMessage('Signed out from all devices successfully!');
    } catch (error) {
      setErrors({ general: 'Failed to sign out all devices. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Password Change Section */}
      <form onSubmit={handlePasswordChange} className="bg-surface rounded-lg border border-border p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Change Password</h3>
          <Icon name="Key" size={20} className="text-text-tertiary" />
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              New Password
            </label>
            <Input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
              error={errors.new_password}
              placeholder="Enter new password"
              required
            />
            <p className="text-xs text-text-tertiary mt-1">
              Password must be at least 8 characters with uppercase, lowercase, numbers, and special characters
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm New Password
            </label>
            <Input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
              error={errors.confirm_password}
              placeholder="Confirm new password"
              required
            />
          </div>
          
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={loading || !passwordData.new_password || !passwordData.confirm_password}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </div>
      </form>

      {/* Security Questions Section */}
      <div className="bg-surface rounded-lg border border-border p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-text-primary">Security Questions</h3>
            {userProfile?.security_questions?.some(q => q.question && q.answer) && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Configured
              </span>
            )}
          </div>
          <Icon name="Shield" size={20} className="text-text-tertiary" />
        </div>

        {editingSecurityQuestions ? (
          <>
            {/* Edit form - shown when editing */}
            <div className="space-y-4">
              {securityQuestions.map((q, index) => (
                <div key={q.id} className="space-y-2 p-4 bg-surface-secondary rounded-lg">
                  <label className="block text-sm font-medium text-text-primary">
                    Question {index + 1} {index === 0 && <span className="text-error">*</span>}
                  </label>
                  <select
                    value={q.question}
                    onChange={(e) => handleSecurityQuestionChange(index, 'question', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary text-sm"
                  >
                    <option value="">Select a security question</option>
                    {PREDEFINED_QUESTIONS.map((question, idx) => (
                      <option key={idx} value={question}>
                        {question}
                      </option>
                    ))}
                  </select>

                  <label className="block text-sm font-medium text-text-primary">
                    Your Answer {index === 0 && <span className="text-error">*</span>}
                  </label>
                  <Input
                    type="text"
                    value={q.answer}
                    onChange={(e) => handleSecurityQuestionChange(index, 'answer', e.target.value)}
                    placeholder="Enter your answer (case-insensitive)"
                  />
                </div>
              ))}

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingSecurityQuestions(false);
                    // Reset to saved data from userProfile
                    const savedQuestions = userProfile?.security_questions?.length > 0 
                      ? userProfile.security_questions 
                      : [
                          { id: 1, question: '', answer: '' },
                          { id: 2, question: '', answer: '' },
                          { id: 3, question: '', answer: '' }
                        ];
                    setSecurityQuestions(savedQuestions);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveSecurityQuestions}
                  loading={loading}
                >
                  Save Questions
                </Button>
              </div>
            </div>
          </>
        ) : userProfile?.security_questions?.some(q => q.question && q.answer) ? (
          <>
            {/* Display verification section if questions are configured */}
            <div className="space-y-4">
              <p className="text-sm text-text-secondary mb-4">
                To update your password, please answer one of your security questions:
              </p>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  {userProfile?.security_questions?.find(q => q.question && q.answer)?.question}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={passwordData.security_answer}
                    onChange={(e) => {
                      setPasswordData({...passwordData, security_answer: e.target.value});
                      setSecurityAnswerVerified(false);
                    }}
                    error={errors.security_answer}
                    placeholder="Enter your answer"
                    disabled={securityAnswerVerified}
                  />
                  <Button
                    type="button"
                    variant={securityAnswerVerified ? 'success' : 'outline'}
                    onClick={handleVerifySecurityAnswer}
                    disabled={securityAnswerVerified || !passwordData.security_answer}
                  >
                    {securityAnswerVerified ? 'Verified ✓' : 'Verify'}
                  </Button>
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowEmailVerification(true);
                    handleSendVerificationEmail();
                  }}
                >
                  <Icon name="Edit" size={16} className="mr-1" />
                  Edit Questions
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Display setup section if questions are not configured */}
            <div className="space-y-4">
              <p className="text-sm text-text-secondary mb-4">
                Set up security questions to add an extra layer of protection when changing your password.
              </p>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEmailVerification(true);
                  handleSendVerificationEmail();
                }}
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Set Up Security Questions
              </Button>
            </div>
          </>
        )}

        {errors.security_questions && (
          <div className="mt-4 bg-error-50 border border-error-200 rounded-lg p-3">
            <div className="flex">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mr-2 mt-0.5" />
              <p className="text-sm text-error">{errors.security_questions}</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Account Security Settings */}
      <form onSubmit={handleSecurityUpdate} className="bg-surface rounded-lg border border-border p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Account Security</h3>
        <div className="space-y-4">
          {/* Two-Factor Authentication */}
          <div className="flex flex-wrap items-center justify-between py-3 border-b border-border">
            <div className="mb-2 md:mb-0 pr-4">
              <h4 className="text-sm font-medium text-text-primary">Two-Factor Authentication</h4>
              <p className="text-xs text-text-secondary">
                Add an extra layer of security to your account
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSecuritySettings({
                ...securitySettings,
                two_factor_auth: !securitySettings.two_factor_auth
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                securitySettings.two_factor_auth ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  securitySettings.two_factor_auth ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {/* Show Login History */}
          <div className="flex flex-wrap items-center justify-between py-3 border-b border-border">
            <div className="mb-2 md:mb-0 pr-4">
              <h4 className="text-sm font-medium text-text-primary">Show Login History</h4>
              <p className="text-xs text-text-secondary">
                View your recent login activity
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={fetchLoginHistory}
              disabled={loading}
            >
              <Icon name="History" size={16} className="mr-2" />
              View History
            </Button>
          </div>
          
          {/* Password Expiry */}
          <div className="py-3 border-b border-border">
            <h4 className="text-sm font-medium text-text-primary mb-2">Password Expiry</h4>
            <p className="text-xs text-text-secondary mb-3">
              Set how often your password should be changed
            </p>
            <select
              value={securitySettings.password_expiry_days}
              onChange={(e) => setSecuritySettings({
                ...securitySettings,
                password_expiry_days: parseInt(e.target.value)
              })}
              className="w-full md:w-auto px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
              <option value="365">365 days</option>
              <option value="0">Never</option>
            </select>
          </div>
          
          {/* Team Security */}
          <div className="flex flex-wrap items-center justify-between py-3 border-b border-border">
            <div className="mb-2 md:mb-0 pr-4">
              <h4 className="text-sm font-medium text-text-primary">Require Team 2FA</h4>
              <p className="text-xs text-text-secondary">
                Require all team members to enable two-factor authentication
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSecuritySettings({
                ...securitySettings,
                require_team_2fa: !securitySettings.require_team_2fa
              })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                securitySettings.require_team_2fa ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  securitySettings.require_team_2fa ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {/* Advanced Security Section */}
          <div className="py-3">
            <h4 className="text-sm font-medium text-text-primary mb-2">Advanced Security</h4>
            <p className="text-xs text-text-secondary mb-4">
              Configure additional security settings
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {}}
              >
                <Icon name="Key" size={16} className="mr-2" />
                Security Questions
              </Button>
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {}}
              >
                <Icon name="Shield" size={16} className="mr-2" />
                IP Restrictions
              </Button>
            </div>
          </div>
        </div>
        
        {errors.general && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4 mt-4">
            <div className="flex">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mr-2" />
              <p className="text-sm text-error">{errors.general}</p>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 mt-4">
            <div className="flex">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
              <p className="text-sm text-success">{successMessage}</p>
            </div>
          </div>
        )}
        
        <div className="flex justify-end mt-6">
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Security Settings'}
          </Button>
        </div>
      </form>
      
      {/* Session Management */}
      <div className="bg-surface rounded-lg border border-border p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary mb-2 md:mb-0">Active Sessions</h3>
          <Button
            type="button"
            variant="danger-outline"
            size="sm"
            onClick={handleSignOutAllDevices}
            disabled={loading}
          >
            <Icon name="LogOut" size={16} className="mr-2" />
            Sign Out All Devices
          </Button>
        </div>
        
        <div className="border-t border-border pt-4 space-y-4">
          {loginHistory.find(entry => entry.is_current) ? (
            <div className="bg-surface-secondary rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="bg-primary-100 p-2 rounded-full">
                    <Icon 
                      name={loginHistory.find(entry => entry.is_current)?.device.includes('iPhone') || loginHistory.find(entry => entry.is_current)?.device.includes('Android') ? 'Smartphone' : 'Monitor'} 
                      size={20} 
                      className="text-primary" 
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-text-primary">
                      {loginHistory.find(entry => entry.is_current)?.device} • {loginHistory.find(entry => entry.is_current)?.browser}
                    </h4>
                    <p className="text-xs text-text-secondary">
                      {loginHistory.find(entry => entry.is_current)?.location} • {loginHistory.find(entry => entry.is_current)?.ip_address}
                    </p>
                    <p className="text-xs text-success mt-1">Active now</p>
                  </div>
                </div>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Current
                </span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-text-secondary">No active sessions found.</p>
          )}
        </div>
      </div>

      {/* Login History Modal/Section */}
      {showLoginHistory && (
        <div className="bg-surface rounded-lg border border-border p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">Login History</h3>
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setShowLoginHistory(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          {loginHistory.length > 0 ? (
            <div className="space-y-4">
              {loginHistory.map((entry) => (
                <div key={entry.id} className="bg-surface-secondary rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full ${entry.is_current ? 'bg-primary-100' : 'bg-secondary-100'}`}>
                        <Icon 
                          name={entry.device.includes('iPhone') || entry.device.includes('Android') ? 'Smartphone' : 'Monitor'} 
                          size={20} 
                          className={entry.is_current ? 'text-primary' : 'text-text-tertiary'} 
                        />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-text-primary">
                          {entry.device} • {entry.browser}
                        </h4>
                        <p className="text-xs text-text-secondary">
                          {entry.location} • {entry.ip_address}
                        </p>
                        <p className="text-xs text-text-tertiary mt-1">
                          {entry.is_current ? 'Active now' : entry.timestamp.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {entry.is_current ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          Current Session
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                          {entry.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-secondary">No login history available.</p>
          )}
        </div>
      )}

      {/* Email Verification Modal */}
      {showEmailVerification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <Icon name="Lock" size={20} className="text-primary" />
                Verify Your Email
              </h3>
              <button
                onClick={handleCancelEmailVerification}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {!verificationCodeSent ? (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">
                  A verification code will be sent to your email address. Please verify it to proceed.
                </p>
                <Button
                  onClick={handleSendVerificationEmail}
                  variant="primary"
                  className="w-full"
                  loading={loading}
                >
                  <Icon name="Mail" size={16} className="mr-2" />
                  Send Verification Code
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">
                  Enter the 6-digit verification code sent to <span className="font-medium">{userProfile?.email}</span>
                </p>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    maxLength="6"
                    value={enteredVerificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      setEnteredVerificationCode(value.slice(0, 6));
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center text-2xl tracking-widest font-mono"
                    disabled={emailVerified}
                  />
                  {emailVerified && (
                    <p className="text-sm text-success mt-2 flex items-center gap-1">
                      <Icon name="CheckCircle" size={16} />
                      Email verified successfully!
                    </p>
                  )}
                </div>

                <p className="text-xs text-text-secondary">
                  Code expires in 10 minutes
                </p>

                {errors.email_verification && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                    <div className="flex">
                      <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mr-2 mt-0.5" />
                      <p className="text-sm text-error">{errors.email_verification}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleCancelEmailVerification}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1"
                    onClick={handleVerifyEmail}
                    loading={loading}
                    disabled={emailVerified}
                  >
                    Verify
                  </Button>
                </div>

                <button
                  type="button"
                  onClick={handleSendVerificationEmail}
                  className="w-full text-sm text-primary hover:text-primary-dark transition-colors py-2"
                >
                  Send code again
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Account Section */}
      <div className="bg-surface rounded-lg border border-border p-4 sm:p-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Delete Account</h3>
            <p className="text-sm text-text-secondary mt-1">Permanently delete your employer account and all associated data</p>
          </div>
          <Icon name="Trash" size={20} className="text-text-tertiary" />
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-error mb-2">What will be deleted:</h4>
                <ul className="text-sm text-error space-y-1 ml-4 list-disc">
                  <li>Your company profile and all information</li>
                  <li>Your job postings and applications</li>
                  <li>Your messages and conversations</li>
                  <li>Your team members and permissions</li>
                  <li>Your billing and subscription information</li>
                  <li>All associated account data</li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="danger"
            onClick={() => {
              setShowDeleteConfirmation(true);
              setDeleteStep(1);
              setDeleteConfirmationText('');
            }}
            disabled={loading}
          >
            <Icon name="Trash" size={16} className="mr-2" />
            Delete My Account
          </Button>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-error flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} />
                Delete Account
              </h3>
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setDeleteStep(1);
                  setDeleteConfirmationText('');
                  setErrors({});
                }}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {deleteStep === 1 ? (
              <div className="space-y-4">
                <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                  <p className="text-sm text-error font-semibold mb-2">⚠️ This action cannot be undone!</p>
                  <p className="text-sm text-error">
                    Deleting your account will permanently remove all your data, including company profile, job postings, messages, and team information.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteStep(1);
                      setDeleteConfirmationText('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    className="flex-1"
                    onClick={handleDeleteAccount}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary">
                  Type <span className="font-mono font-semibold text-error">DELETE MY ACCOUNT</span> to confirm deletion:
                </p>
                <div>
                  <Input
                    type="text"
                    value={deleteConfirmationText}
                    onChange={(e) => {
                      setDeleteConfirmationText(e.target.value);
                      setErrors({});
                    }}
                    placeholder="Type confirmation text here"
                    autoFocus
                  />
                  {errors.delete_account && (
                    <p className="text-sm text-error mt-2">{errors.delete_account}</p>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setDeleteStep(1);
                      setDeleteConfirmationText('');
                      setErrors({});
                    }}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    className="flex-1"
                    onClick={handleDeleteAccount}
                    loading={loading}
                    disabled={loading || deleteConfirmationText !== 'DELETE MY ACCOUNT'}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4 mt-6">
          <div className="flex">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mr-2" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;